/**
 * Shared client-error suppression list.
 *
 * One source of truth for "errors we never want to alert on": browser-extension
 * DOM interference, React hydration noise, framework navigation throws, expected
 * unauthenticated states, and third-party script noise.
 *
 * Consumed by BOTH:
 *   - `src/lib/error-reporting.ts` — the Discord/DB reporter, and
 *   - `src/lib/analytics/posthog.ts` — the PostHog `$exception` `before_send`
 *     filter (native Error Tracking auto-captures uncaught errors; this keeps
 *     the Issues list free of extension/hydration noise).
 *
 * Keep this list dependency-free so it can be imported from anywhere without
 * pulling in the Supabase client or posthog-js.
 */
export const SUPPRESSED_ERROR_PATTERNS = [
  // Browser extension / translation DOM interference. Chrome and Firefox name
  // the DOM method in the message; WebKit does NOT (see `isDomMutationError`
  // below, which reads the stack) — its NotFoundError message is the generic
  // "The object can not be found here."
  "setSelectionRange",
  "ResizeObserver loop",
  "removeChild",
  "insertBefore",
  "is not a child of this node",
  "The object can not be found here",
  // React hydration noise
  "Hydration failed",
  "hydrating",
  "Text content did not match",
  "Minified React error #418",
  // Next.js internal navigation
  "NEXT_NOT_FOUND",
  "NEXT_REDIRECT",
  // Stale-deploy chunk fetches. The tab is running the previous build and asks
  // for a hashed chunk the new deploy removed — not a code fault. The client
  // reloads itself instead (src/lib/chunk-recovery.ts), so alerting on these
  // just pages an admin every time we ship.
  "ChunkLoadError",
  "Loading chunk",
  "Failed to load chunk",
  "Loading CSS chunk",
  "error loading dynamically imported module",
  "Failed to fetch dynamically imported module",
  "Importing a module script failed",
  // Third-party script noise
  "Script error.",
  "Non-Error promise rejection captured",
  // Supabase auth session missing (expected unauthenticated state)
  "Auth session missing",
  "session_not_found",
  // Supabase auth rate-limit ("For security purposes, you can only request this after N seconds")
  // User-facing validation, not a system error. The form already surfaces the
  // toast — and since 2026-07-31 these are NOT invisible: the auth flows call
  // `reportIfRateLimited` (src/lib/rate-limit-telemetry.ts), which records a
  // PostHog `rate_limited` event plus a rolled-up row in `rate_limit_hits`
  // (admin Logs hub). Keep them suppressed here; they are not crashes.
  "For security purposes, you can only request",
  "you can only request this after",
  // SSR-only context-missing throws — useSession/useProfile/useSubscription
  // legitimately need their providers, but the throw fires when Next.js
  // tries to prerender a partial tree that doesn't include the provider
  // (server actions, error fallbacks). User-facing renders recover on
  // hydration.
  "useSession must be used within",
  "useProfile must be used within",
  "useSubscription must be used within",

  // ── Expected user outcomes, not application faults ──────────────────────
  // Added 2026-09-01 after auditing `platform_events`: 38% of all recorded
  // app_errors were these. Every one is a state the UI already explains with a
  // toast, and every one was also paging Discord — which is how a real crash
  // (a null `display_name` on /seller/earnings) sat unnoticed among 47 wrong
  // passwords. Suppressing them here is what makes the remaining errors legible.
  //
  // Sign-in failures are still *counted* — PostHog receives the auth events, and
  // repeated failures roll up in `rate_limit_hits` — so a credential-stuffing
  // spike is still visible as a security signal rather than as 47 crash reports.
  "Invalid login credentials",
  "Email not confirmed",
  "email rate limit exceeded",
  "Email link is invalid or has expired",
  "Token has expired or is invalid",
  "User already registered",
  // Server-side validation the form surfaces itself. The listing rules are
  // working when these fire; they are not faults.
  "needs a downloadable file",
  "Your account is blocked from commissions",
  "Connect your Stripe payout account",
  // Crypto-wallet browser extensions injecting into every page.
  "Failed to connect to MetaMask",
  "MetaMask",
  "window.ethereum",
] as const;

export function isSuppressedErrorMessage(message: string | null | undefined): boolean {
  if (!message) return false;
  return SUPPRESSED_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

/**
 * Stack frames that mean the error did not come from our code.
 *
 * Message matching cannot catch these: a browser extension throws
 * `Cannot read properties of undefined (reading 'M_ID')`, which is
 * indistinguishable from one of our own null-access bugs until you read the
 * stack. That single extension produced 32 reports — the second-largest error
 * on the platform — all from `chrome-extension://.../executors/200.js`.
 *
 * We cannot fix code we do not ship, and we cannot ask users to uninstall
 * their extensions, so these are noise by definition.
 */
const FOREIGN_STACK_ORIGINS = [
  "chrome-extension://",
  "moz-extension://",
  "safari-extension://",
  "safari-web-extension://",
  "webkit-masked-url:",
] as const;

/** True when the error originates in a browser extension rather than our app. */
function isForeignStack(stack: string | null | undefined): boolean {
  if (!stack) return false;
  return FOREIGN_STACK_ORIGINS.some((origin) => stack.includes(origin));
}

// ── Foreign DOM mutation ─────────────────────────────────────────────────────
//
// React owns the nodes it renders. When something else — Google Translate,
// Safari's built-in translation, a password manager, an ad blocker — rewrites
// or removes those nodes, React's next commit calls `removeChild` /
// `insertBefore` on a parent that no longer holds the child and the browser
// throws. Nothing in our code is wrong, and the fix is to retry the render,
// not to show a crash screen or page an admin.
//
// The message is browser-specific, which is how 2026-09-06's /cart and
// /dashboard/purchases reports slipped past the pattern list: Chrome says
// "Failed to execute 'removeChild' on 'Node': The node to be removed is not a
// child of this node", Firefox says "Node.removeChild: The node to be removed
// is not a child of this node", but WebKit says only "The object can not be
// found here." with the method name in the STACK (`removeChild@[native code]`).
// So this classifier reads message, name and stack together.

const DOM_MUTATION_METHODS = ["removeChild", "insertBefore", "replaceChild", "appendChild"] as const;

/** WebKit/Safari and Firefox frame for a native DOM method: `removeChild@[native code]`. */
const WEBKIT_NATIVE_DOM_FRAME = new RegExp(
  String.raw`\b(${DOM_MUTATION_METHODS.join("|")})@\[native code\]`,
);
/** Chromium frame for the same: `at Node.removeChild (<anonymous>)` / `at HTMLElement.insertBefore`. */
const CHROMIUM_NATIVE_DOM_FRAME = new RegExp(
  String.raw`\bat [A-Za-z]*(?:Node|Element)\.(${DOM_MUTATION_METHODS.join("|")})\b`,
);
/** DOMException names the browser raises for a node that is not where React left it. */
const DOM_MUTATION_EXCEPTION_NAMES = new Set(["NotFoundError", "HierarchyRequestError"]);
/** Message fragments shared by every engine's phrasing of the same failure. */
const DOM_MUTATION_MESSAGE_FRAGMENTS = [
  "removechild",
  "insertbefore",
  "replacechild",
  "is not a child of this node",
  "the object can not be found here",
  "the node before which the new node is to be inserted is not a child",
];

/**
 * True when an error is the browser refusing a DOM mutation because a node
 * React expected to own has been moved or removed by something outside React.
 *
 * `name` is the DOMException name (`error.name`); it is enough on its own only
 * together with a native DOM frame in the stack, because a `NotFoundError` can
 * also come from e.g. a Range API call in our own code.
 */
export function isDomMutationError(
  message: string | null | undefined,
  stack?: string | null,
  name?: string | null,
): boolean {
  const lower = (message ?? "").toLowerCase();
  if (DOM_MUTATION_MESSAGE_FRAGMENTS.some((fragment) => lower.includes(fragment))) return true;

  const hasNativeDomFrame =
    !!stack && (WEBKIT_NATIVE_DOM_FRAME.test(stack) || CHROMIUM_NATIVE_DOM_FRAME.test(stack));
  if (!hasNativeDomFrame) return false;

  // A native removeChild/insertBefore frame on its own is already the
  // signature (our code never calls these directly — React does); the
  // exception name only confirms it.
  return name == null || name === "" || DOM_MUTATION_EXCEPTION_NAMES.has(name) || name === "Error";
}

/**
 * The single question the reporters ask: is this worth alerting on?
 * Checks the message against the pattern list, the stack for a foreign
 * origin, and both for a foreign DOM mutation, so neither reporter has to
 * remember to do all three.
 */
export function isSuppressedError(
  message: string | null | undefined,
  stack?: string | null,
  name?: string | null,
): boolean {
  return (
    isSuppressedErrorMessage(message) ||
    isForeignStack(stack) ||
    isDomMutationError(message, stack, name)
  );
}
